import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { Contact, ContactDetail, PaginatedResponse } from "@/lib/types"

interface ContactFilters {
  search?: string
  owner_id?: string
  department?: string
  company_id?: string
  created_at_from?: string
  created_at_to?: string
  sort?: string
  cursor?: string
  limit?: number
}

function buildParams(filters: ContactFilters): string {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      params.set(key, String(value))
    }
  })
  return params.toString()
}

export function useContacts(filters: ContactFilters = {}) {
  return useQuery({
    queryKey: ["contacts", filters],
    queryFn: () => {
      const qs = buildParams(filters)
      return api.get<PaginatedResponse<Contact>>(`/api/v1/contacts${qs ? `?${qs}` : ""}`)
    },
  })
}

export function useContact(id: string | undefined) {
  return useQuery({
    queryKey: ["contacts", id],
    queryFn: () => api.get<{ data: ContactDetail }>(`/api/v1/contacts/${id}`),
    enabled: !!id,
  })
}

export function useCreateContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ data: Contact }>("/api/v1/contacts", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
    },
  })
}

export function useUpdateContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch<{ data: Contact }>(`/api/v1/contacts/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
    },
  })
}

export function useDeleteContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/contacts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] })
    },
  })
}
